import useTranslation from 'next-translate/useTranslation'
import { useEffect } from 'react'

import Breadcrumbs from '@/atoms/Breadcrumb'
import PRODUCT_AS_FANSI_KIND from '@/constants/productAsFansiKind'
import PRODUCT_AS_URL from '@/constants/productAsUrl'
import pushBack from '@/helpers/pushBack'
import CreatorBar from '@/molecules/CreatorBar'
import ExperienceCard from '@/molecules/ExperienceCard'
import ExperienceEntryCard from '@/molecules/ExperienceEntryCard'
import ProductDescription from '@/molecules/ProductDescription'
import ShoppingCard from '@/molecules/ShoppingCard'
import GTM from '@/services/GTM'
import { Media } from '@/templates/Media/Media'

import ExperienceInstructions from '../../components/molecules/ExperienceInstructions'
import HowItWorks from './includes/HowItWorks'
import OtherProducts from './includes/OtherProducts'
import Presentation from './includes/Presentation'
import HowToParticipate from './includes/Presentation/includes/HowToParticipate'
import Reviews from './includes/Reviews'
import { ProductViewProps } from './ts'

function ProductView({ product, creatorProducts }: ProductViewProps) {
  const { t } = useTranslation('global')

  if (!product || !creatorProducts) return null

  const {
    creator,
    description,
    contest_instructions,
    type,
    tags,
    terms,
    reviews,
    rating,
    votes,
  } = product

  const filteredProducts = creatorProducts.filter(({ id }) => id !== product.id)

  useEffect(() => GTM.pushProduct(product), [])

  return (
    <div>
      <Breadcrumbs
        crumbs={[
          {
            label: `${t(PRODUCT_AS_FANSI_KIND[type])}`,
            url: `/products/${PRODUCT_AS_URL[type]}`,
          },
          { label: `${product.name}` },
        ]}
        className="mb-8"
      />
      <CreatorBar
        className="paded-layout"
        creator={creator}
        onBack={() => pushBack()}
      />
      <div className="box">
        <div className="flex flex-col items-start justify-between mt-8 space-y-8 xl:space-x-24 md:mt-14 xl:space-y-0 xl:flex-row">
          <Presentation creator={creator} product={product} />
          {type !== 'Product::FansiExperience' ? (
            <div className="w-full xl:sticky xl:top-12 xl:max-w-xl xl:w-1/2 2xl:max-w-[580px]">
              <ShoppingCard product={product} />
            </div>
          ) : (
            <div className="flex flex-col w-full space-y-8 xl:max-w-xl 2xl:max-w-[580px]">
              <ExperienceCard product={product} />
              {product?.entries?.map((entry) => (
                <ExperienceEntryCard
                  key={entry.id}
                  product={product}
                  entry={entry}
                />
              ))}
            </div>
          )}
        </div>
        <Media lessThan="xl">
          <ProductDescription
            value={description}
            embedded={product?.embedded_link}
            tags={tags}
          />
          <ExperienceInstructions
            instructions={contest_instructions}
            termsHref={terms}
          />
          <HowToParticipate type={type} />
        </Media>
        <HowItWorks type={type} />
      </div>
      <Reviews
        reviews={reviews}
        rating={rating?.aggregate?.avg?.value}
        votes={votes?.aggregate?.count}
      />
      <OtherProducts creator={creator} products={filteredProducts} />
    </div>
  )
}

export default ProductView
